import React, { memo } from 'react';
import { Form } from 'antd';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import LoadingSpinner from 'components/LoadingSpinner';
import { isRealHts as realHTS } from '@xbcb/entry-utils/dist/lib/htsUtil';
import {
  isDeepThoughtClassificationEnabled,
  isHtsReasonabilityEnabled,
  isTariff232AutomationFeatureEnabled,
  isTariffRedesignEnabled,
} from 'featureFlags';
import LineItemHTSInput from 'components/LineItemHTSInput';
import type { RemoveAdditionalTariff } from 'libs/tariffs';
import { CommercialInvoiceBaseProps } from 'types';
import { getCountryCodeFromWorkOrderType } from 'libs/workOrderType/getCountryCodeFromWorkOrderType';
import { pascalCase } from 'change-case';
import { WorkOrderType } from '@xbcb/work-order-types';
import { HTS_INDICES } from 'libs/htsIndices';
import { useAppRecordContext } from 'context/appRecordContext';
import { useCurrentUser } from 'libs/hooks';
import { useHtsClassification } from 'libs/hooks/useHtsClassification';
import { useReasonabilityLineState } from 'libs/hooks/useReasonabilityLineState';
import LineItemReasonabilityCheck from 'components/LineItemReasonabilityCheck';
import { StyledButton, ButtonRow } from './styles';
import { HtsClassificationDrawer } from './HtsClassification/HtsClassificationDrawer';
import { HtsRecommendationResult } from './HtsClassification/HtsRecommendationResult';
import { HtsQuestionsPrompt } from './HtsClassification/HtsQuestionsPrompt';
import { StyledHtsAlert } from './HtsClassification/styles';
import { ReasoningSection } from './HtsClassification/ReasoningSection';
import LineItemTariffDisplayV2 from 'components/LineItemTariffDisplayV2';

export interface LineItemTariffsProps extends CommercialInvoiceBaseProps {
  removeAdditionalTariff: RemoveAdditionalTariff;
}

export const LineItemTariffs: React.FC<LineItemTariffsProps> = (props) => {
  const { fullNamePath, readOnly, workOrderType } = props;
  const form = Form.useFormInstance();
  const [bundle] = useBundle('components.HtsClassification');
  const [tariffBundle] = useBundle('components.LineItemTariffDisplayV2');
  const { recordId: entryId } = useAppRecordContext();
  const { id: userId } = useCurrentUser();

  const linesIdx = fullNamePath.indexOf('lines');
  const lineIndex = (linesIdx >= 0 ? fullNamePath[linesIdx + 1] : 0) as number;

  const tariffs = Form.useWatch([...fullNamePath, 'tariffs'], form);
  const description = Form.useWatch([...fullNamePath, 'description'], form);

  // Navigate from line level (invoices.X.products.Y.lines.Z) up to product level
  const linesKeyIdx = fullNamePath.lastIndexOf('lines');
  const productNamePath =
    linesKeyIdx >= 0 ? fullNamePath.slice(0, linesKeyIdx) : fullNamePath;
  // Read synchronously so the product query starts on the first render.
  const productId = form.getFieldValue([...productNamePath, 'product', 'id']);
  const lines = Form.useWatch([...productNamePath, 'lines'], form);

  const {
    classificationState,
    validateLoading,
    submitLoading,
    mountLoading,
    productDetailsReady,
    handlers,
  } = useHtsClassification({
    description,
    destinationCountryCode: getCountryCodeFromWorkOrderType(
      pascalCase(workOrderType || '') as WorkOrderType,
    ),
    entryId,
    lineIndex,
    userId,
    form,
    fullNamePath,
    productId,
    tariffs,
  });

  const handleClear = () => {
    form.setFields([{ name: [...fullNamePath, 'tariffs'], value: [] }]);
    // When reasonability is enabled, clearing the HTS also removes the AI
    // results tied to it: drop the REASONABILITY / HTS_CLASSIFICATION compliance
    // checks and reset the classification (reasonability card + validation).
    if (isHtsReasonabilityEnabled) {
      const remainingChecks = (
        (form.getFieldValue([...fullNamePath, 'complianceChecks']) as
          | { type?: string }[]
          | undefined) ?? []
      ).filter(
        (check) =>
          check?.type !== 'HTS_CLASSIFICATION' &&
          check?.type !== 'REASONABILITY',
      );
      form.setFields([
        { name: [...fullNamePath, 'complianceChecks'], value: remainingChecks },
        { name: [...fullNamePath, 'classificationCache'], value: undefined },
      ]);
      handlers.resetClassification();
    }
    if (isTariff232AutomationFeatureEnabled) {
      form.setFieldValue([...fullNamePath, 'candidateAdditionalTariffs'], []);
      form.setFieldValue(
        [...fullNamePath, 'productCharacteristics'],
        undefined,
      );
    }
  };

  const hasRealHts = tariffs?.some(
    (tariff: { htsNumber?: string }) =>
      tariff?.htsNumber && realHTS(tariff.htsNumber),
  );
  const primaryHts = tariffs?.[tariffs.length - 1]?.htsNumber;

  // Show "Validate HTS Code" button when feature is enabled and user has entered a valid HTS number and CBP description
  const showValidateButton =
    isDeepThoughtClassificationEnabled(workOrderType) &&
    hasRealHts &&
    description &&
    !readOnly;

  const showQuestionsPrompt =
    !!classificationState.questions?.length && !classificationState.result;

  // Reasonability card state + auto-validate for a failed line.
  const {
    showReasonabilityCard,
    reasonabilityStatus,
    htsValidationPassed,
    htsValidationReasoning,
    details: reasonabilityDetails,
  } = useReasonabilityLineState({
    linePath: fullNamePath,
    workOrderType,
    classificationState,
    showValidateButton: Boolean(showValidateButton),
    productDetailsReady,
    classificationLoading: mountLoading || validateLoading,
    userId,
    onValidate: handlers.handleValidateHTS,
  });

  return (
    <div>
      {isTariffRedesignEnabled ? (
        <LineItemTariffDisplayV2
          tariffFullNamePath={[...fullNamePath, 'tariffs']}
          tariffLocalNamePath={[...props.localNamePath, 'tariffs']}
          readOnly={readOnly}
          disabled={props.disabled}
          required={props.required}
          showValidateButton={showValidateButton && !showReasonabilityCard}
          validateLoading={validateLoading}
          onValidateHts={handlers.handleValidateHTS}
          lineIndex={lineIndex}
          lineCount={Array.isArray(lines) ? lines.length : 1}
        />
      ) : (
        HTS_INDICES.map((i) => (
          <LineItemHTSInput index={i} key={i} {...props} />
        ))
      )}

      {!htsValidationPassed && classificationState.confirmationAlert && (
        <StyledHtsAlert
          type={classificationState.confirmationAlert.type}
          showIcon
          message={classificationState.confirmationAlert.message}
          description={
            classificationState.confirmationAlert.reasoningTrace?.length ? (
              <ReasoningSection
                reasoningTrace={
                  classificationState.confirmationAlert.reasoningTrace
                }
              />
            ) : undefined
          }
        />
      )}

      {!showReasonabilityCard && classificationState.result && (
        <HtsRecommendationResult
          result={classificationState.result}
          onAccept={handlers.handleAcceptRecommendation}
          onDecline={handlers.handleDeclineRecommendation}
        />
      )}

      <ButtonRow>
        {showValidateButton &&
          !isTariffRedesignEnabled &&
          !showReasonabilityCard && (
            <div>
              <StyledButton
                onClick={handlers.handleValidateHTS}
                loading={validateLoading}
              >
                {safeGetMessage(bundle, 'hts_validate_button')}
              </StyledButton>
              {mountLoading && <LoadingSpinner size="small" />}
            </div>
          )}

        {tariffs?.length ? (
          <StyledButton onClick={handleClear}>
            {safeGetMessage(
              tariffBundle,
              'clear_all_hts',
              undefined,
              'Clear all HTS numbers',
            )}
          </StyledButton>
        ) : null}
      </ButtonRow>

      {showReasonabilityCard && (
        <LineItemReasonabilityCheck
          status={reasonabilityStatus}
          currentHts={primaryHts}
          recommendedHts={classificationState.result?.tariffCode}
          confidence={classificationState.result?.confidence}
          onValidateHts={
            showValidateButton ? handlers.handleValidateHTS : undefined
          }
          validateLoading={validateLoading}
          onAcceptRecommendation={handlers.handleAcceptRecommendation}
          onDeclineOverride={handlers.handleDeclineRecommendation}
          htsValidationPassed={htsValidationPassed}
          htsValidationConfidence={
            classificationState.confirmationAlert?.confidence
          }
          htsValidationReasoning={htsValidationReasoning}
          rationale={reasonabilityDetails.rationale}
          reasonabilityConfidence={reasonabilityDetails.confidence}
          detailsLoading={reasonabilityDetails.loading}
        />
      )}

      {showQuestionsPrompt && (
        <HtsQuestionsPrompt onOpenDrawer={handlers.handleOpenDrawer} />
      )}

      <HtsClassificationDrawer
        classificationState={classificationState}
        submitLoading={submitLoading}
        validateLoading={validateLoading}
        onClose={handlers.handleCloseDrawer}
        onSubmitAnswers={handlers.handleSubmitAnswers}
        onRestartClassification={handlers.handleRestartClassification}
        onAnswerChange={handlers.handleAnswerChange}
      />
    </div>
  );
};

export default memo(LineItemTariffs);
